import * as yup from 'yup';

import { TIN_LENGTH } from '@/constants/common';
import { EMAIL_PATTERN, TIN_PATTERN } from '@/constants/patterns';

import { getTranslatedMessage } from '@/i18n/utils';

import { ResidenceType } from '@/types/user';

export const MINOR_INTERNET_BANKING_ELIGIBILITY_AGE = 0; //13;

export const getMinorOtherValidationSchema = () => {
  return yup.object().shape({
    relationship: yup
      .string()
      .required(getTranslatedMessage('minorOthers.form.relationship.required')),
    hasPhone: yup
      .string()
      .required(getTranslatedMessage('minorOthers.form.hasPhone.required'))
      .oneOf(['yes', 'no']),
    email: yup.string().when('hasPhone', {
      is: 'yes',
      then: schema =>
        schema
          .required(getTranslatedMessage('minorOthers.form.email.required'))
          .matches(
            new RegExp(EMAIL_PATTERN),
            getTranslatedMessage('minorOthers.form.email.pattern'),
          ),
      otherwise: schema => schema.notRequired(),
    }),
    phoneCode: yup.string(),
    phoneNumber: yup.string().when('hasPhone', {
      is: 'yes',
      then: schema =>
        schema.matches(
          /^\d{7}$/,
          getTranslatedMessage('minorOthers.form.phone.pattern'),
        ),
      otherwise: schema => schema.notRequired(),
    }),
  });
};

export type MinorOtherFormType = yup.InferType<
  ReturnType<typeof getMinorOtherValidationSchema>
>;

export const DEFAULT_MINOR_OTHER_FORM_VALUES: MinorOtherFormType = {
  relationship: '',
  hasPhone: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
};

export type MinorMailingFormFields = {
  mailingSameAsResidential?: boolean;
  mailingLine1?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  mailingCountry?: string;
  residenceCountry?: string;
};

export type MinorAddressFormType = {
  shareAddress: boolean;
  childLivesWithPersonName?: string;
  childLivesWithRelationship?: string;
  childLivesWithRelationshipDescription?: string;
  line1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  residence?: ResidenceType;
  differentTaxResidency: boolean;
  taxResidences: string[];
  usTin: string;
  localTin: string;
  otherTins: Record<string, string>;
} & MinorMailingFormFields;

export const getMinorProofOfAddressValidationSchema = (
  t: (key: string, params?: Record<string, string>) => string,
  options: {
    hasResidenceOptions: boolean;
    shouldAddMailingAddress?: boolean;
    hasChildLivesWithRelationshipOptions?: boolean;
  },
) => {
  const {
    hasResidenceOptions,
    shouldAddMailingAddress,
    hasChildLivesWithRelationshipOptions,
  } = options;

  const createTinValidation = (minLen: number, maxLen: number) => {
    return yup
      .string()
      .test(
        'tin-pattern',
        t('createNewAccount.addressPage.form.tin.error.pattern'),
        value => !value || new RegExp(TIN_PATTERN).test(value),
      )
      .test(
        'tin-min-length',
        t('createNewAccount.addressPage.form.tin.error.minimum', {
          value: `${minLen}`,
        }),
        value => !value || value.length >= minLen,
      )
      .test(
        'tin-max-length',
        t('createNewAccount.addressPage.form.tin.error.maximum', {
          value: `${maxLen}`,
        }),
        value => !value || value.length <= maxLen,
      );
  };

  return yup.object().shape({
    shareAddress: yup.boolean().required(),
    childLivesWithPersonName: hasChildLivesWithRelationshipOptions
      ? yup.string().when('shareAddress', {
          is: false,
          then: schema =>
            schema
              .required(t('minorPoA.childLivesWith.name.error.required'))
              .max(
                40,
                t('minorPoA.childLivesWith.error.maximum', { value: '40' }),
              ),
          otherwise: schema => schema.notRequired(),
        })
      : yup.string().notRequired(),
    childLivesWithRelationship: hasChildLivesWithRelationshipOptions
      ? yup.string().when('shareAddress', {
          is: false,
          then: schema =>
            schema.required(
              t('minorPoA.childLivesWith.relationship.error.required'),
            ),
          otherwise: schema => schema.notRequired(),
        })
      : yup.string().notRequired(),
    childLivesWithRelationshipDescription: hasChildLivesWithRelationshipOptions
      ? yup.string().when(['shareAddress', 'childLivesWithRelationship'], {
          is: (shareAddress: boolean, relationship: string) =>
            !shareAddress && relationship === 'OTHER',
          then: schema =>
            schema
              .required(t('minorPoA.childLivesWith.description.error.required'))
              .max(
                40,
                t('minorPoA.childLivesWith.error.maximum', { value: '40' }),
              ),
          otherwise: schema => schema.notRequired(),
        })
      : yup.string().notRequired(),
    line1: yup.string().when('shareAddress', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema =>
        schema.required(
          t('createNewAccount.addressPage.form.address.error.required'),
        ),
    }),
    city: yup.string().when('shareAddress', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema =>
        schema.required(
          t('createNewAccount.addressPage.form.city.error.required'),
        ),
    }),
    state: yup.string(),
    zip: yup.string(),
    country: yup.string().when('shareAddress', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema =>
        schema.required(
          t('createNewAccount.addressPage.form.country.error.required'),
        ),
    }),
    residence: hasResidenceOptions
      ? yup
          .string()
          .required(
            t('createNewAccount.addressPage.form.residence.error.required'),
          )
      : yup.string(),
    differentTaxResidency: yup.boolean(),
    taxResidences: yup
      .array()
      .of(yup.string())
      .when(['shareAddress', 'differentTaxResidency'], {
        is: (shareAddress: boolean, differentTaxResidency: boolean) =>
          shareAddress || differentTaxResidency,
        then: schema =>
          schema
            .min(
              1,
              t(
                'createNewAccount.addressPage.form.countryOfTax.error.required',
              ),
            )
            .required(
              t(
                'createNewAccount.addressPage.form.countryOfTax.error.required',
              ),
            ),
        otherwise: schema => schema.notRequired(),
      }),
    localTin: createTinValidation(TIN_LENGTH.otherMin, TIN_LENGTH.otherMax),
    usTin: createTinValidation(TIN_LENGTH.usMin, TIN_LENGTH.usMax),
    otherTins: yup.lazy(obj =>
      yup.object(
        Object.keys(obj || {}).reduce(
          (acc, key) => ({
            ...acc,
            [key]: createTinValidation(
              TIN_LENGTH.otherMin,
              TIN_LENGTH.otherMax,
            ),
          }),
          {},
        ),
      ),
    ),
    ...(shouldAddMailingAddress
      ? {
          mailingSameAsResidential: yup.boolean(),
          mailingLine1: yup
            .string()
            .when(['shareAddress', 'mailingSameAsResidential'], {
              is: (shareAddress: boolean, mailingSameAsResidential: boolean) =>
                !shareAddress && mailingSameAsResidential === false,
              then: schema =>
                schema.required(
                  t('createNewAccount.addressPage.form.address.error.required'),
                ),
              otherwise: schema => schema.notRequired(),
            }),
          mailingCity: yup
            .string()
            .when(['shareAddress', 'mailingSameAsResidential'], {
              is: (shareAddress: boolean, mailingSameAsResidential: boolean) =>
                !shareAddress && mailingSameAsResidential === false,
              then: schema =>
                schema.required(
                  t('createNewAccount.addressPage.form.city.error.required'),
                ),
              otherwise: schema => schema.notRequired(),
            }),
          mailingState: yup.string(),
          mailingZip: yup.string(),
          mailingCountry: yup
            .string()
            .when(['shareAddress', 'mailingSameAsResidential'], {
              is: (shareAddress: boolean, mailingSameAsResidential: boolean) =>
                !shareAddress && mailingSameAsResidential === false,
              then: schema =>
                schema.required(
                  t('createNewAccount.addressPage.form.country.error.required'),
                ),
              otherwise: schema => schema.notRequired(),
            }),
          residenceCountry: yup
            .string()
            .required(
              t(
                'createNewAccount.addressPage.form.residenceCountry.error.required',
              ),
            ),
        }
      : {}),
  });
};

export const DEFAULT_MINOR_MAILING_FORM_VALUES: MinorMailingFormFields = {
  mailingSameAsResidential: true,
  mailingLine1: '',
  mailingCity: '',
  mailingState: '',
  mailingZip: '',
  mailingCountry: '',
  residenceCountry: '',
};

export const DEFAULT_MINOR_ADDRESS_FORM_VALUES: MinorAddressFormType = {
  shareAddress: false,
  childLivesWithPersonName: '',
  childLivesWithRelationship: '',
  childLivesWithRelationshipDescription: '',
  line1: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  residence: undefined,
  differentTaxResidency: false,
  taxResidences: [],
  usTin: '',
  localTin: '',
  otherTins: {},
  ...DEFAULT_MINOR_MAILING_FORM_VALUES,
};
